import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { startOfDay } from 'date-fns';
import { useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';
import useFetch from 'hooks/fetch';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { setInvestPortfolioInfo } from 'actions/walkthrough';
import useGenerateValues from '../components/Chart/hooks/useGenerateValues';
import { parse } from '../components/Chart/utils/extremes';
import { getPortfolioChart } from '../actions';
import { generateFakePortfolioData, mapStockToPortfolioChartPeriod, mapPortfolioToStockChartPeriod, logInvest, } from '../utils';
import { prepareChartPathProviderData, getProfitLoss } from '../utils/portfolio';
import useAccount from './useAccount';
import { useFakeData } from '../debug';
import { selectActiveTradingAccountId } from '../reducers/selectors';
import { PORTFOLIO_CHART_DEFAULT_PERIOD } from '../constants';
let contextValueGlobal;
const usePortfolioChartData = () => {
    const account = useAccount();
    const isAlpaca = account.broker === 'alpaca';
    const [profitLoss, setProfitLoss] = useState(account.growth?.value || 0);
    const [profitLossPct, setProfitLossPct] = useState(account.growthPercent || 0); // for selected period
    const dispatch = useAppDispatch();
    const accountId = useAppSelector(selectActiveTradingAccountId);
    const dataCache = useRef({
        '1D': null,
        '1W': null,
        '1M': null,
        '3M': null,
        YTD: null,
        '1Y': null,
        ALL: null,
    });
    const values = useGenerateValues();
    const dotStyle = useAnimatedStyle(() => ({
        opacity: values.dotScale.value,
        transform: [{ translateX: values.positionX.value }],
    }), [values.dotScale, values.positionX]);
    const data = useSharedValue({
        '1D': [],
        '1W': [],
        '1M': [],
        '3M': [],
        YTD: [],
        '1Y': [],
        MAX: [],
    });
    const dataFiltered = useSharedValue({
        '1D': [],
        '1W': [],
        '1M': [],
        '3M': [],
        YTD: [],
        '1Y': [],
        MAX: [],
    });
    const originalDataSet = useSharedValue({
        '1D': [],
        '1W': [],
        '1M': [],
        '3M': [],
        YTD: [],
        '1Y': [],
        MAX: [],
    });
    const defaultPeriod = useMemo(() => (isAlpaca ? PORTFOLIO_CHART_DEFAULT_PERIOD : 'ALL'), [isAlpaca]);
    const prevPeriod = useSharedValue(null);
    const currPeriod = useSharedValue(mapPortfolioToStockChartPeriod(defaultPeriod));
    /* Three constants below are not used, but required in type */
    const currentPriceCircleRadius = useSharedValue(0);
    const colorOverrides = useSharedValue({
        '1D': undefined,
        '1W': undefined,
        '1M': undefined,
        '3M': undefined,
        YTD: undefined,
        '1Y': undefined,
        MAX: undefined,
    });
    const originalDataShared = useSharedValue({
        timestamp: [],
        equity: [],
        profitLoss: [],
        profitLossPct: [],
    });
    const isFirstRun = useRef(true);
    const currentPeriod = useRef(mapStockToPortfolioChartPeriod(undefined));
    const currentMode = useRef('P/L');
    if (isFirstRun.current) {
        contextValueGlobal = {
            dotStyle,
            ...values,
            providedData: { points: [], smoothingStrategy: 'simple' },
            originalData: { timestamp: [], equity: [], profitLoss: [], profitLossPct: [] },
            originalDataShared,
            providedEquity: { points: [], smoothingStrategy: 'simple' },
            providedPl: { points: [], smoothingStrategy: 'simple' },
            providedPlPercent: { points: [], smoothingStrategy: 'simple' },
            data,
            dataFiltered,
            prevPeriod,
            currPeriod,
            originalDataSet,
            currentPriceCircleRadius,
            colorOverrides,
        };
        isFirstRun.current = false;
    }
    const [loading, setLoading] = useState(true);
    const [fetch] = useFetch();
    const load = useCallback(async (dateRange = mapPortfolioToStockChartPeriod(defaultPeriod), options) => {
        const period = mapStockToPortfolioChartPeriod(dateRange);
        if (!options?.cacheOnly) {
            currentPeriod.current = period;
        }
        const cache = dataCache.current[period];
        if (cache !== null && !options?.forceUpdate) {
            if (options?.cacheOnly)
                return;
            const { original, providedEquity, providedPl, providedPlPercent } = cache;
            contextValueGlobal = {
                ...contextValueGlobal,
                providedData: providedEquity,
                providedEquity,
                providedPl,
                providedPlPercent,
                originalData: original,
            };
            originalDataShared.value = original;
            prevPeriod.value = currPeriod.value;
            contextValueGlobal.progress.value = 0;
            currPeriod.value = dateRange;
            contextValueGlobal.progress.value = withTiming(1);
            const priceChange = getProfitLoss(original.profitLoss);
            const priceChangePct = getProfitLoss(original.profitLossPct);
            setProfitLoss(priceChange);
            setProfitLossPct(priceChangePct);
            dispatch(setInvestPortfolioInfo({
                priceChange,
                priceChangePct,
            }));
            return;
        }
        const cacheOnly = options?.cacheOnly;
        let shouldSetLoadingToFalse = false;
        if (!cacheOnly && !options?.forceUpdate) {
            setLoading(true);
            shouldSetLoadingToFalse = true;
        }
        try {
            const params = { period, accountId };
            const response = useFakeData ? generateFakePortfolioData(params) : await fetch(getPortfolioChart(params));
            if (response) {
                let firstValidIndex = 0;
                if (account?.timestamp) {
                    // cutoff all points before date of opening trading account
                    const minTimestamp = startOfDay(new Date(account.timestamp)).getTime() / 1000;
                    firstValidIndex = response.chart.timestamp.findIndex((timestamp) => timestamp !== null && timestamp >= minTimestamp);
                }
                if (response.chart.timestamp.length < 2) {
                    // add one or two points to make charts possible - they are lines, not dots
                    const now = Math.floor(new Date().getTime() / 1000);
                    if (response.chart.timestamp.length === 0 || response.chart.timestamp[0] === null) {
                        // case with no valid dots at all. Make it up using current time and zeros for everything
                        response.chart.timestamp = [now];
                    }
                    if (response.chart.equity.length === 0 || response.chart.equity[0] === null) {
                        response.chart.equity[0] = 0;
                    }
                    if (response.chart.profitLoss.length === 0 || response.chart.profitLoss[0] === null) {
                        response.chart.profitLoss[0] = 0;
                    }
                    if (response.chart.profitLossPct.length === 0 || response.chart.profitLossPct[0] === null) {
                        response.chart.profitLossPct[0] = 0;
                    }
                    // now there is one point, make another one by copying existing
                    response.chart.timestamp.unshift(response.chart.timestamp[0] - 3600);
                    response.chart.equity.unshift(response.chart.equity[0]);
                    response.chart.profitLoss.unshift(response.chart.profitLoss[0]);
                    response.chart.profitLossPct.unshift(response.chart.profitLossPct[0]);
                }
                const chart = {
                    timestamp: response.chart.timestamp.slice(firstValidIndex),
                    equity: response.chart.equity.slice(firstValidIndex),
                    profitLoss: response.chart.profitLoss.slice(firstValidIndex),
                    profitLossPct: response.chart.profitLossPct.slice(firstValidIndex),
                };
                const equityData = prepareChartPathProviderData(response.chart);
                const plData = prepareChartPathProviderData(response.chart, { for: 'portfolio', key: 'profitLoss' });
                const plPercentData = prepareChartPathProviderData(response.chart, {
                    for: 'portfolio',
                    key: 'profitLossPct',
                });
                const provided = plData;
                const [parsedData, parsedDataNullable] = parse(provided.points, provided.yRange);
                const [, parsedOriginalData] = parse(provided.nativePoints || provided.points);
                dataCache.current[period] = {
                    original: chart,
                    provided,
                    providedPl: plData,
                    providedEquity: equityData,
                    providedPlPercent: plPercentData,
                    parsedData,
                    parsedDataNullable,
                    parsedOriginalData,
                };
                data.value = {
                    '1D': dataCache.current['1D'] ? dataCache.current['1D'].parsedDataNullable : [],
                    '1W': dataCache.current['1W'] ? dataCache.current['1W'].parsedDataNullable : [],
                    '1M': dataCache.current['1M'] ? dataCache.current['1M'].parsedDataNullable : [],
                    '3M': dataCache.current['3M'] ? dataCache.current['3M'].parsedDataNullable : [],
                    YTD: dataCache.current['YTD'] ? dataCache.current['YTD'].parsedDataNullable : [],
                    '1Y': dataCache.current['1Y'] ? dataCache.current['1Y'].parsedDataNullable : [],
                    MAX: dataCache.current['ALL'] ? dataCache.current['ALL'].parsedDataNullable : [],
                    [dateRange]: parsedDataNullable,
                };
                dataFiltered.value = {
                    '1D': dataCache.current['1D'] ? dataCache.current['1D'].parsedData : [],
                    '1W': dataCache.current['1W'] ? dataCache.current['1W'].parsedData : [],
                    '1M': dataCache.current['1M'] ? dataCache.current['1M'].parsedData : [],
                    '3M': dataCache.current['3M'] ? dataCache.current['3M'].parsedData : [],
                    YTD: dataCache.current['YTD'] ? dataCache.current['YTD'].parsedData : [],
                    '1Y': dataCache.current['1Y'] ? dataCache.current['1Y'].parsedData : [],
                    MAX: dataCache.current['ALL'] ? dataCache.current['ALL'].parsedData : [],
                    [dateRange]: parsedData,
                };
                originalDataSet.value = {
                    '1D': dataCache.current['1D'] ? dataCache.current['1D'].parsedOriginalData : [],
                    '1W': dataCache.current['1W'] ? dataCache.current['1W'].parsedOriginalData : [],
                    '1M': dataCache.current['1M'] ? dataCache.current['1M'].parsedOriginalData : [],
                    '3M': dataCache.current['3M'] ? dataCache.current['3M'].parsedOriginalData : [],
                    YTD: dataCache.current['YTD'] ? dataCache.current['YTD'].parsedOriginalData : [],
                    '1Y': dataCache.current['1Y'] ? dataCache.current['1Y'].parsedOriginalData : [],
                    MAX: dataCache.current['ALL'] ? dataCache.current['ALL'].parsedOriginalData : [],
                    [dateRange]: parsedOriginalData,
                };
                if (!cacheOnly) {
                    if (period === currentPeriod.current) {
                        contextValueGlobal = {
                            ...contextValueGlobal,
                            providedData: provided,
                            providedEquity: equityData,
                            providedPl: plData,
                            providedPlPercent: plPercentData,
                            originalData: chart,
                        };
                        originalDataShared.value = chart;
                        prevPeriod.value = currPeriod.value;
                        contextValueGlobal.progress.value = 0;
                        currPeriod.value = dateRange;
                        contextValueGlobal.progress.value = withTiming(1);
                        const priceChange = getProfitLoss(chart.profitLoss);
                        const priceChangePct = getProfitLoss(chart.profitLossPct);
                        setProfitLoss(priceChange);
                        setProfitLossPct(priceChangePct);
                        dispatch(setInvestPortfolioInfo({
                            priceChange,
                            priceChangePct,
                        }));
                    }
                    if (dateRange === undefined) {
                        // dateRange is undefined only for initial refresh for default period
                        Object.keys(dataCache.current).forEach((period) => {
                            if (dataCache.current[period] === null) {
                                load(mapPortfolioToStockChartPeriod(period), {
                                    cacheOnly: true,
                                });
                            }
                        });
                    }
                }
                if (!dateRange || shouldSetLoadingToFalse) {
                    setLoading(false);
                }
            }
            else if (!dateRange || shouldSetLoadingToFalse) {
                throw new Error();
            }
        }
        catch {
            logInvest(`Failed to fetch portfolio chart data`, false, 'red');
            if (!dateRange || shouldSetLoadingToFalse) {
                // Modal.showError('Failed to load chart. Please try again later.');
                setLoading(false);
            }
        }
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [account?.timestamp]);
    const changeMode = useCallback((mode) => {
        if (mode)
            currentMode.current = mode;
        const cache = dataCache.current[currentPeriod.current];
        if (dataCache.current && cache && contextValueGlobal)
            switch (mode) {
                case 'Equity':
                    contextValueGlobal = {
                        ...contextValueGlobal,
                        providedData: cache.providedEquity,
                    };
                    // setData(cache.providedEquity);
                    break;
                case 'P/L':
                    contextValueGlobal = {
                        ...contextValueGlobal,
                        providedData: cache.providedPl,
                    };
                    // setData(cache.providedPl);
                    break;
                // case 'P/L %':
                //   contextValueGlobal = {
                //     ...contextValueGlobal,
                //     providedData: cache.providedPlPercent,
                //   };
                //   setData(cache.providedPlPercent);
                //   break;
                default:
                    contextValueGlobal = {
                        ...contextValueGlobal,
                        providedData: cache.providedPl,
                    };
                // setData(cache.providedPl);
            }
    }, []);
    useEffect(() => {
        load();
    }, [load]);
    const update = useCallback(async () => {
        try {
            const priorityPeriod = currentPeriod.current;
            await load(mapPortfolioToStockChartPeriod(priorityPeriod), { cacheOnly: false, forceUpdate: true });
            Object.keys(dataCache.current).forEach((period) => {
                if (period && period !== priorityPeriod) {
                    load(mapPortfolioToStockChartPeriod(period), {
                        cacheOnly: true,
                        forceUpdate: true,
                    });
                }
            });
        }
        catch {
            // not so important
        }
    }, [load]);
    return [profitLoss, profitLossPct, currentPeriod.current, loading, contextValueGlobal, load, changeMode, update];
};
export default usePortfolioChartData;
